/**
 * ThreeStoreExample — what three stores pay, worked from the ONE arithmetic (APP-ENTITLEMENT-001; owner ruling
 * Q-AE-26 and the plan's "three-store worked example"): the featured anchor at three locations, one of them with
 * a fourth register so the register-beyond-three line shows, and the site's own app beside it — monthly and
 * annual (exactly ten months, Q-AE-26) side by side, before tax. A `<table id="three-stores">` with a caption; the
 * signup basket links here, never retypes it.
 *
 * @module @puffin-core/marketing-shell/pricing/ThreeStoreExample
 */

import PropTypes from 'prop-types';
import { ANNUAL_MONTHS_CHARGED, annualFromMonthly, getApp } from '@puffin-core/app-catalog';
import { unitsKeyOf, useAccountAppsUiTranslate } from '@puffin-core/account-apps-ui/copy';

import { SectionHeader } from '../components/SectionHeader.jsx';
import { useMarketingShellTranslate } from '../hooks/useMarketingShellTranslate.js';
import { exampleQuoteOf, quoteLineText } from './pricingRules.js';

/** The section id the signup basket's "See a three-store example" link lands on. */
export const THREE_STORE_EXAMPLE_ID = 'three-stores';

function ExampleLineRow({ line, formatPrice, t, tApps }) {
  const key = `${line.appKey}-${line.dimensionKey ?? 'app'}`;
  return (
    <tr data-testid={`three-store-line-${key}`}>
      <th scope="row">{getApp(line.appKey).label}</th>
      <td>{quoteLineText({ line, t, tApps, formatPrice, unitsKeyOf })}</td>
      <td>{formatPrice(line.monthlyCents)}</td>
      <td>{formatPrice(annualFromMonthly(line.monthlyCents))}</td>
    </tr>
  );
}

ExampleLineRow.propTypes = {
  line: PropTypes.object.isRequired,
  formatPrice: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  tApps: PropTypes.func.isRequired,
};

function ExampleTotalRow({ quote, formatPrice, t }) {
  return (
    <tr data-testid="three-store-total">
      <th scope="row" colSpan={2}>
        {t('pricing.totalRow')}
      </th>
      <td>{t('pricing.totalMonthly', { amount: formatPrice(quote.monthlyCents) })}</td>
      <td>
        {t('pricing.totalAnnual', {
          amount: formatPrice(quote.annualCents),
          monthly: formatPrice(quote.annualMonthlyEquivalentCents),
        })}
      </td>
    </tr>
  );
}

ExampleTotalRow.propTypes = {
  quote: PropTypes.object.isRequired,
  formatPrice: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

function ExampleTable({ quote, caption, formatPrice, t, tApps }) {
  return (
    <table className="comparison-table" data-testid="three-store-example">
      <caption>{caption}</caption>
      <thead>
        <tr>
          <th scope="col">{t('pricing.columnApp')}</th>
          <th scope="col">{t('pricing.columnLine')}</th>
          <th scope="col">{t('pricing.columnMonthly')}</th>
          <th scope="col">{t('pricing.columnAnnual')}</th>
        </tr>
      </thead>
      <tbody>
        {quote.lines.map((line) => (
          <ExampleLineRow
            key={`${line.appKey}-${line.dimensionKey ?? 'app'}`}
            line={line}
            formatPrice={formatPrice}
            t={t}
            tApps={tApps}
          />
        ))}
      </tbody>
      <tfoot>
        <ExampleTotalRow quote={quote} formatPrice={formatPrice} t={t} />
      </tfoot>
    </table>
  );
}

ExampleTable.propTypes = {
  quote: PropTypes.object.isRequired,
  caption: PropTypes.string.isRequired,
  formatPrice: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  tApps: PropTypes.func.isRequired,
};

/**
 * @param {Object} props
 * @param {import('./pricingRules.js').PublicCatalog} props.catalog
 * @param {string} props.anchorAppKey - the featured anchor.
 * @param {string} props.siteAppKey
 * @param {(cents: number) => string} props.formatPrice
 * @param {Function} [props.t]
 * @returns {React.JSX.Element|null} nothing when the example cannot be priced (a price not yet published).
 */
export function ThreeStoreExample({ catalog, anchorAppKey, siteAppKey, formatPrice, t: hostT }) {
  const t = useMarketingShellTranslate(hostT);
  const tApps = useAccountAppsUiTranslate(hostT);
  const { basket, quote } = exampleQuoteOf(catalog, anchorAppKey, siteAppKey);
  if (quote === null) return null;
  const anchorLabel = getApp(anchorAppKey).label;
  const caption =
    basket.appKeys.length > 0
      ? t('pricing.exampleCaption', {
          anchor: anchorLabel,
          apps: basket.appKeys.map((key) => getApp(key).label).join(', '),
        })
      : t('pricing.exampleCaptionAnchorOnly', { anchor: anchorLabel });
  const headingId = `${THREE_STORE_EXAMPLE_ID}-heading`;
  return (
    <section className="section-marketing" id={THREE_STORE_EXAMPLE_ID} aria-labelledby={headingId}>
      <SectionHeader
        heading={t('pricing.exampleHeading')}
        subheading={t('pricing.annualColumnNote', { months: String(ANNUAL_MONTHS_CHARGED) })}
        headingId={headingId}
      />
      <div className="comparison-table-wrapper scroll-affordance">
        <ExampleTable
          quote={quote}
          caption={caption}
          formatPrice={formatPrice}
          t={t}
          tApps={tApps}
        />
      </div>
    </section>
  );
}

ThreeStoreExample.propTypes = {
  catalog: PropTypes.shape({ apps: PropTypes.arrayOf(PropTypes.object).isRequired }).isRequired,
  anchorAppKey: PropTypes.string.isRequired,
  siteAppKey: PropTypes.string.isRequired,
  formatPrice: PropTypes.func.isRequired,
  t: PropTypes.func,
};

export default ThreeStoreExample;
